import React from "react";
import { Link } from "react-router-dom";
import { useBridgeMatches } from "@/hooks/bridge/useBridgeMatches";
import { Skeleton } from "@/components/ui/skeleton";
import { Callout, DHead, Pill, Tag, TagTone } from "@/components/ms";
import { FigLink, HeadLink } from "./chrome";

const CONFIDENCE_TONE: Record<string, TagTone> = {
  high: "pos",
  medium: "warn",
  low: "neg",
};

export const BridgeMatchesWidget = () => {
  // Fetch only suggested matches (pending confirmation)
  const { data, isLoading } = useBridgeMatches(0, { status: 'suggested' });

  const matches = data?.items || [];
  const suggestedCount = matches.length;
  const totalRemaining = data?.remaining || 0;
  const totalSuggested = suggestedCount + totalRemaining;

  // Loading state
  if (isLoading) {
    return (
      <section className="flex flex-col">
        <DHead label="Bridges & transfers" />
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 border-t border-ms-line py-[11px] first:border-t-0">
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </section>
    );
  }

  // Empty state - no bridge transfers yet
  if (totalSuggested === 0) {
    return (
      <section className="flex flex-col">
        <DHead label="Bridges & transfers" />
        <Callout className="mt-3.5">
          <b>Bridges aren't sales.</b> Moonscape matches cross-chain sends to their
          receives across Ethereum, Arbitrum, Polygon and more, so a bridge never shows
          up as a disposal. Nothing is waiting for you right now.
        </Callout>
        <div className="mt-[var(--gap)] flex flex-wrap gap-3">
          <Pill as={Link} to="/settings/exchange/new">
            Connect exchange
          </Pill>
          <Pill as={Link} to="/wallets" variant="ghost">
            Add wallet
          </Pill>
        </div>
      </section>
    );
  }

  // Get top 3 matches for preview
  const previewMatches = matches.slice(0, 3);

  return (
    <section className="flex flex-col">
      <DHead
        label="Bridges & transfers"
        right={<HeadLink to="/bridge-matches">Review all</HeadLink>}
      />

      <Callout tone="tan" className="mt-3.5">
        <b>
          {totalSuggested} transfer{totalSuggested !== 1 ? "s" : ""} need{totalSuggested === 1 ? "s" : ""} confirmation.
        </b>{" "}
        Confirming bridges and transfers between your own accounts keeps them from ever counting as disposals.
      </Callout>

      <div className="mt-3.5 flex flex-col">
        {previewMatches.map((match, index) => (
          <FigLink
            key={match.id || index}
            to="/bridge-matches"
            label={match.bridge_name || (match.bridge_contract_id != null ? "Unknown bridge" : "Internal transfer")}
            sub={
              <>
                {match.send_transaction?.amount} {match.send_transaction?.asset} ·{" "}
                {match.send_transaction?.blockchain} → {match.receive_transaction?.blockchain || "unknown chain"}
                {match.send_transaction?.timestamp
                  ? ` · ${new Date(match.send_transaction.timestamp).toLocaleDateString()}`
                  : ""}
              </>
            }
            tag={
              <Tag tone={CONFIDENCE_TONE[match.confidence_level] || "warn"}>
                {match.confidence_level}
              </Tag>
            }
          />
        ))}
      </div>

      {totalSuggested > 3 && (
        <HeadLink to="/bridge-matches" className="mt-3.5 inline-flex min-h-[40px] items-center">
          {totalSuggested - 3} more match{totalSuggested - 3 !== 1 ? "es" : ""}
        </HeadLink>
      )}
    </section>
  );
};
